'use client';

import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import { RadioGroup } from '@koda/koda-components/molecules/RadioGroup';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { Artist } from '../../../../../../../src/lib/api/models/artists';
import ArtistInput from '../../../components/input/ArtistInput';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import {
  CONCERT_AND_EVENT_CONTENT_PATH,
  concertAndEventBusinessInformationSchema,
  createConcertAndEventDefaults,
  musicSources,
} from './concertAndEvent.Schema';
import { CurrentAndPreviousYear } from './consts/dateIntervals';

export const ConcertAndEventForm = (): ReactElement => {
  const licenseCode = LicenseTypes.ConcertAndEvent;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: concertAndEventBusinessInformationSchema,
    defaults: createConcertAndEventDefaults,
  });

  const t = useTranslations(CONCERT_AND_EVENT_CONTENT_PATH);

  return (
    <>
      <DatePicker
        value={values.date ? new Date(values.date) : undefined}
        label={t('date.label')}
        name={t('date.label')}
        onChange={date => setField('date', date ? dayjs(date).format('YYYY-MM-DD') : '')}
        validated={!!values.date && !getError('date')}
        placeholder={t('date.placeholder')}
        initialVisibleValue={dayjs().toDate()}
        interval={CurrentAndPreviousYear}
        error={values.date || showErrors ? getError('date') : undefined}
      />
      <ArtistInput
        value={values.artist}
        onChange={(artist?: Artist) => {
          setField('artist', artist?.number ?? '');
          setField('artistName', artist?.name ?? '');
        }}
        name={values.artistName}
        error={values.artist || showErrors ? getError('artist') : undefined}
      />
      <NumberField
        label={t('revenue.label')}
        name={t('revenue.label')}
        placeholder={t('revenue.placeholder')}
        required
        helpText={t('revenue.helpText')}
        disabled={costCalculated}
        value={values.revenue}
        onChange={e => setField('revenue', parseInt(e.target.value))}
        error={values.revenue || showErrors ? getError('revenue') : undefined}
        validated={!!values.revenue && !getError('revenue')}
        styleVariant="border"
        formatThousands
      />
      <NumberField
        label={t('attendees.label')}
        name={t('attendees.label')}
        placeholder={t('attendees.placeholder')}
        required
        disabled={costCalculated}
        value={values.attendees}
        onChange={e => setField('attendees', parseInt(e.target.value))}
        error={values.attendees !== undefined || showErrors ? getError('attendees') : undefined}
        validated={!!values.attendees && !getError('attendees')}
        formatThousands
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <RadioGroup
        title={t('primaryMusicSource.label')}
        options={musicSources.map(source => ({
          value: source,
          label: t(`primaryMusicSource.options.${source}`),
        }))}
        disabled={costCalculated}
        value={values.primaryMusicSource}
        horizontal={false}
        className="gap-4"
        onValueChange={value => setField('primaryMusicSource', value as (typeof musicSources)[number])}
      />
    </>
  );
};
